// @flow
import React from 'react';
import useSheet from 'app/jss';
import * as Enter from 'app/utils/animations/enter';
import * as Leave from 'app/utils/animations/leave';
import Avatar from 'app/components/commons/Avatar';
import ConfirmBox from 'app/components/commons/ConfirmBox';
import FriendBannerInfo from 'app/components/friends/banners/FriendsBannerInfo';
import BouncyLoader from 'app/components/commons/BouncyLoader';
import Icon from 'app/components/commons/Icon';
import AnimatedGroup from 'app/components/commons/AnimatedGroup';
import t from 'app/utils/translate';

type Props = {
  friend: iFriendUser,
  isDone?: boolean,
  isCreating?: boolean,
  onCancel: () => void,
  onConfirm: () => void,
  classes: Object,
};

class InviteFriendDesktop extends React.Component {
  props: Props;

  static defaultProps = {
    isDone: false,
    isCreating: false,
  };

  render() {
    const { friend, onCancel, onConfirm, classes, isDone, isCreating } = this.props;

    return (
      <FriendBannerInfo icon="icon-invite-friend" className={classes.mainContainer}>
        <div className={classes.avatarBox}>
          <Avatar status={friend.status} size="small" avatar={friend.avatar} />
          <span className={classes.username}>{friend.username}</span>
        </div>
        <div className={classes.textContainer}>
          <span className={classes.heading}>UH OH!</span>
          <span className={classes.subheading}>
            {t("It seems the friend you're looking for is not registered yet.")}
          </span>
        </div>
        <AnimatedGroup onEnter={Enter.appear(0.3, 0.3)} onLeave={Leave.disappear(0.3)}>
          {!isCreating &&
            !isDone && (
              <div className={classes.bottomContainer} key="send">
                <span className={classes.question}>{t('Do you want to send an invitation?')}</span>
                <ConfirmBox
                  className={classes.confirmBox}
                  onConfirm={onConfirm}
                  gap={30}
                  size={30}
                />
              </div>
            )}
          {isDone && (
            <div className={classes.bottomContainer} key="done">
              <span className={classes.question}>{t('Request Sent!')}</span>
              <Icon type="icon-tick-circle" size={30} className={classes.doneTick} />
            </div>
          )}
          {isCreating && (
            <div className={classes.bottomContainer} key="creating">
              <span className={classes.question}>{t('Sending your request')}</span>
              <div className={classes.loader}>
                <BouncyLoader scheme="dark" />
              </div>
            </div>
          )}
        </AnimatedGroup>
      </FriendBannerInfo>
    );
  }
}

function applyStyles(theme: Theme) {
  const avatarBoxWidth = 140;
  return {
    loader: {
      width: 40,
    },
    doneTick: {
      color: theme.color.primary,
    },
    username: {
      fontSize: theme.fontSize.xxsmall,
      textOverflow: 'ellipsis',
      maxWidth: '90%',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      marginTop: 5,
    },
    mainContainer: {
      justifyContent: 'center',
      alignItems: 'center',
    },
    textContainer: {
      display: 'flex',
      flexDirection: 'column',
      padding: 10,
      alignItems: 'center',
      textAlign: 'center',
    },
    heading: {
      fontSize: theme.fontSize.large,
      color: theme.color.basis6,
    },
    subheading: {
      color: theme.color.basis4,
      fontSize: theme.fontSize.small,
    },
    avatarBox: {
      width: avatarBoxWidth,
      height: avatarBoxWidth,
      backgroundColor: theme.color.white,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
    },
    question: {
      textTransform: 'uppercase',
      color: theme.color.basis6,
      fontWeight: 600,
      marginBottom: 10,
      fontSize: theme.fontSize.xsmall,
      textAlign: 'center',
    },
    bottomContainer: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
    },
  };
}

export default useSheet(applyStyles)(InviteFriendDesktop);
